var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useContext, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography, } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { AuthContext } from "@/providers/AuthContextProvider";
import { RESUME_KEY, resumeFor, writeResume } from "@/lib/signing/request";
import { useSigningFormat } from "./format";
/**
 * "Next member signs in": records the handover, leaves a note in session
 * storage and signs out, returning to this page. After the next member signs
 * in, SigningProvider reads the note and reopens the request for them.
 */
export var SigningHandoverLauncher = function (_a) {
    var data = _a.data, api = _a.api, storage = _a.storage;
    var t = useTranslation().t;
    var format = useSigningFormat(data.time_zone);
    var logout = useContext(AuthContext).logout;
    var titleId = useId();
    var _b = useState(false), open = _b[0], setOpen = _b[1];
    var _c = useState(false), busy = _c[0], setBusy = _c[1];
    var _d = useState(false), failed = _d[0], setFailed = _d[1];
    var expiresAt = data.request.expires_at;
    var handOver = function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    setBusy(true);
                    setFailed(false);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, 4, 5]);
                    // The note is written first: storage that refuses it stops the handover
                    // before it is logged. Signing out returns to this page.
                    writeResume(storage !== null && storage !== void 0 ? storage : window.sessionStorage, resumeFor(data.request));
                    return [4 /*yield*/, api.handover(data.request.id)];
                case 2:
                    _b.sent();
                    logout(window.location.href);
                    return [3 /*break*/, 5];
                case 3:
                    _a = _b.sent();
                    try {
                        ;
                        (storage !== null && storage !== void 0 ? storage : window.sessionStorage).removeItem(RESUME_KEY);
                    }
                    catch (_c) {
                        // Storage is unavailable: nothing was written.
                    }
                    setFailed(true);
                    return [3 /*break*/, 5];
                case 4:
                    setBusy(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    return (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "outlined", startIcon: _jsx(LogoutIcon, {}), onClick: function () { return setOpen(true); }, children: t("signing.panel.handover") }), _jsxs(Dialog, { open: open, onClose: function () { return !busy && setOpen(false); }, "aria-labelledby": titleId, children: [_jsx(DialogTitle, { id: titleId, children: t("signing.widget.handoverDialog.title") }), _jsxs(DialogContent, { children: [_jsx(Typography, { children: expiresAt
                                    ? t("signing.dialog.handover", {
                                        time: format.time(expiresAt),
                                    })
                                    : t("signing.widget.handoverDialog.noExpiry") }), failed ? (_jsx(Alert, { severity: "error", sx: { mt: 2 }, children: t("signing.widget.handoverDialog.error") })) : null] }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: function () { return setOpen(false); }, disabled: busy, children: t("signing.widget.handoverDialog.back") }), _jsx(Button, { variant: "contained", startIcon: busy ? (_jsx(CircularProgress, { size: 16, color: "inherit", "aria-hidden": true })) : (_jsx(LogoutIcon, {})), disabled: busy, onClick: function () {
                                    handOver().catch(function () { return undefined; });
                                }, children: t("signing.widget.handoverDialog.confirm") })] })] })] }));
};
